import { detectGranularity, fillGranularity, granularityLabel } from '../../SampleGrouping/Operations/DateGranularity.js'
import { axisLabelOf } from './Selector/axisLabel.js'

type AggRow = { bucketKey: string; bucketLabel: string; sampleCount?: number; data: Record<string, number> }
/**
 * `sampleCount` rides beside `data`, never inside it: the trait branch of the area/line sinks builds
 * its rows by spreading everything that is not `xAxisvalue` into `data`, so a count placed on a flat
 * row would come back as a phantom taxon series.
 */
type ChartRow = { xAxisvalue: string; xAxisLabel: string; sampleCount?: number; data: Record<string, number> }

/**
 * The value a bucket was grouped on, read back out of its key.
 *
 * `computeKeyAndLabel` builds the key as `<field>[<value>]`, nesting an enclosing bucket ahead of it,
 * so the last bracketed segment is this bucket's own value. Decoding rather than plumbing the value
 * through every transformer, but kept honest by `GroupOperation`'s tests asserting the key shape.
 */
export const groupValueOfKey = (bucketKey: string): string | null => bucketKey.match(/\[([^\]]*)\]$/)?.[1] ?? null

/**
 * Spaces a date-grouped axis evenly across the whole period rather than only where samples fell, by
 * adding an all-zero row for each interval nothing was measured in.
 *
 * Only applies to a *projected* date grouping — month, quarter or year — because those values are
 * uniform, one per equal span. A raw timestamp grouping has no interval to step by, and every other
 * kind of grouping has no order to fill, so both are returned untouched.
 *
 * Deliberately a chart concern rather than a grouping one: the bucket list also feeds beta diversity,
 * which compares buckets pairwise, and UpSet, which draws them as sets. Neither has any use for a
 * bucket holding no samples.
 */
export const withContinuousDateAxis = (aggRows: AggRow[], keys: string[], xAxisLabel: string): ChartRow[] => {
  const asRow = (r: AggRow): ChartRow => ({ xAxisvalue: axisLabelOf(r.bucketLabel), xAxisLabel, sampleCount: r.sampleCount, data: r.data })
  if (aggRows.length < 2) {
    return aggRows.map(asRow)
  }

  const values = aggRows.map((r) => groupValueOfKey(r.bucketKey))
  if (values.some((v) => v === null)) {
    return aggRows.map(asRow)
  }

  const present = values as string[]
  const granularity = detectGranularity(present)
  if (!granularity) {
    return aggRows.map(asRow)
  }

  /*
   * Gap-filling keys rows by their date value, so it only holds when that value identifies the bucket.
   * Under a second grouping it does not: `habitat[FOREST]::date[2026-03]` and `habitat[WETLAND]::date[2026-03]`
   * both read as `2026-03`, and the map kept whichever came last — one habitat silently vanished from the
   * chart, and from the numbers table and CSV built off the same rows. Leave a nested grouping alone; the
   * axis it wants is the bucket list itself.
   */
  if (new Set(present).size !== present.length) {
    return aggRows.map(asRow)
  }

  const rowByValue = new Map(present.map((v, i) => [v, aggRows[i]]))
  const empty = Object.fromEntries(keys.map((k) => [k, 0]))

  return fillGranularity(present, granularity).map((value) => {
    const row = rowByValue.get(value)
    // A filled interval genuinely holds no samples, so 0 is the truth here — not the `undefined`
    // that means "no bucket stood behind this row".
    return row ? asRow(row) : { xAxisvalue: granularityLabel(value, granularity), xAxisLabel, sampleCount: 0, data: { ...empty } }
  })
}
